import { Component, OnDestroy, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { IconComponent } from '../../../../shared/icons/icon.component';
import { ScenarioAnimationComponent } from '../../../../shared/components/scenario-animation/scenario-animation.component';
import {
  DEMO_OPTIONS,
  DemoLeafOption,
  DemoLeafOptionCopy,
  DemoOption,
  INITIAL_SCENARIO_ID,
} from '../../config/demo-options';
import { CardComponent } from '../../../../shared/components/card/card.component';
import { ProcessingVisualComponent } from '../processing-visual/processing-visual.component';
import { Store } from '@ngrx/store';
import { ApiScenarioResponseDto } from '@birco/rainmesh-api';
import { ScenarioActions } from '../../state/scenario.actions';
import { selectScenarioState } from '../../state/scenario.selectors';
import { ShowcaseSessionService } from '../../../../core/services/showcase-session.service';
import { LocalAuthService } from '@sso/ld-sso-with-oauth2-oidc';

enum DemoStep {
  PROCESSING_INITIAL = 'processing-initial',
  SELECTION = 'selection',
  OPTION_SELECTION = 'option-selection',
  PROCESSING_FINAL = 'processing-final',
  SUGGESTION = 'suggestion',
  SUCCESS = 'success',
}

@Component({
  selector: 'app-dialog-demo',
  imports: [
    MatDialogModule,
    TranslatePipe,
    IconComponent,
    CardComponent,
    ScenarioAnimationComponent,
    ProcessingVisualComponent,
  ],
  templateUrl: './dialog-demo.component.html',
  styleUrl: './dialog-demo.component.scss',
})
export class DialogDemoComponent implements OnDestroy {
  readonly DemoStep = DemoStep;
  private static readonly PROCESSING_POST_DELAY_MS = 2000;
  private static readonly SUCCESS_AUTO_CLOSE_MS = 60000;

  private readonly dialogRef = inject(MatDialogRef<DialogDemoComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly translateService = inject(TranslateService);
  private readonly store = inject(Store);
  private _showcaseSessionService = inject(ShowcaseSessionService);
  private _localAuthService = inject(LocalAuthService);

  step: DemoStep = DemoStep.PROCESSING_INITIAL;
  selectedCategory: DemoOption | null = null;
  selectedOption: DemoLeafOption | null = null;
  options: DemoOption[] = [];
  processingProgressPercent = 0;
  processingDurationMs = 10000;
  processingElapsedMs = 0;
  successAutoCloseRemainingSeconds = 60;

  private initialScenarioTriggered = false;
  private followedSuggestion = false;
  private runningScenarioId: string | null = null;
  private timerId: ReturnType<typeof setTimeout> | null = null;
  private transitionTimerId: ReturnType<typeof setTimeout> | null = null;
  private successAutoCloseTimerId: ReturnType<typeof setTimeout> | null = null;
  private successAutoCloseIntervalId: ReturnType<typeof setInterval> | null =
    null;
  private animationFrameId: number | null = null;

  get processingElapsedDisplay(): string {
    return this.formatDuration(this.processingElapsedMs);
  }

  get processingDurationDisplay(): string {
    return this.formatDuration(this.processingDurationMs);
  }

  get hasSecondLevelSelection(): boolean {
    return (
      !!this.selectedCategory &&
      !!this.selectedOption &&
      this.selectedCategory !== this.selectedOption
    );
  }

  constructor() {
    this.store
      .select(selectScenarioState)
      .pipe(takeUntilDestroyed())
      .subscribe((state) => {
        if (!state) {
          return;
        }
        const scenarios = Object.values(state.entities);
        const scenariosById = new Map(
          scenarios.map((scenario) => [scenario.id, scenario]),
        );
        this.options = this.resolveOptions(scenariosById);
        this.runningScenarioId = state.runningScenario?.scenarioId ?? null;
        this.triggerInitialScenario(scenariosById, state.isLoading);
      });
  }

  ngOnDestroy() {
    this.clearTimers();
    if (
      this.runningScenarioId &&
      this.step !== DemoStep.SUCCESS &&
      this.step !== DemoStep.SUGGESTION
    ) {
      this.store.dispatch(
        ScenarioActions.public.stopScenario({ id: this.runningScenarioId }),
      );
    }
  }

  onBack() {
    if (this.step === DemoStep.OPTION_SELECTION) {
      this.selectedCategory = null;
      this.selectedOption = null;
      this.step = DemoStep.SELECTION;
    } else {
      this.onClose();
    }
  }

  onSelectOption(option: DemoOption) {
    this.followedSuggestion = false;
    this.selectedCategory = option;
    this.selectedOption = null;

    if (this.isGroupOption(option)) {
      if (option.options.length > 0) {
        this.step = DemoStep.OPTION_SELECTION;
      }
      return;
    }

    this.startScenario(option);
  }

  onSelectSubOption(option: DemoLeafOption) {
    this.startScenario(option);
  }

  onSelectSuggestedNext(target: DemoOption) {
    this.followedSuggestion = true;
    this.selectedCategory = target;
    this.selectedOption = null;

    if (this.isGroupOption(target)) {
      if (target.options.length > 0) {
        this.step = DemoStep.OPTION_SELECTION;
      }
      return;
    }

    this.startScenario(target);
  }

  onSkipSuggestion() {
    this.step = DemoStep.SUCCESS;
    this.startSuccessAutoCloseTimer();
  }

  // Drives the success-screen "chained" suggestion (see nextSuggestion config
  // on demo-options.ts): shown once per chain link, suppressed after the user
  // has already followed one suggestion so the storytelling doesn't loop.
  get nextSuggestion(): { option: DemoOption; messageKey: string } | null {
    if (this.followedSuggestion) {
      return null;
    }

    const suggestion = this.selectedCategory?.nextSuggestion;
    if (!suggestion) {
      return null;
    }

    const target = this.findOptionById(suggestion.targetId);
    return target ? { option: target, messageKey: suggestion.messageKey } : null;
  }

  onLogout() {
    this._showcaseSessionService
      .endSession()
      .subscribe(() => this._localAuthService.logout());
  }

  onClose() {
    if (this.step === DemoStep.SUCCESS) {
      this.clearSuccessAutoCloseTimer();
      this.dialogRef.close(true);
      return;
    }

    const confirmData: ConfirmDialogData = {
      title: this.translateService.instant('demo.dialog.closeConfirm.title'),
      body: this.translateService.instant('demo.dialog.closeConfirm.body'),
      hint: this.translateService.instant('demo.dialog.closeConfirm.hint'),
      confirmLabel: this.translateService.instant(
        'demo.dialog.closeConfirm.confirm',
      ),
      cancelLabel: this.translateService.instant('default.back'),
      action: 'danger',
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: confirmData })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed === true) {
          this.dialogRef.close(false);
        }
      });
  }

  private startScenario(option: DemoLeafOption) {
    this.selectedOption = option;
    if (option.id) {
      this.store.dispatch(
        ScenarioActions.public.runScenario({ id: option.id }),
      );
    }
    this.startLoading(DemoStep.SUCCESS, option.loadingDurationSeconds * 1000);
  }

  private isGroupOption(
    option: DemoOption,
  ): option is Extract<DemoOption, { options: DemoLeafOption[] }> {
    return 'options' in option && Array.isArray(option.options);
  }

  // Resolves a nextSuggestion target by its backend scenario id, checking
  // both top-level options and group sub-options - lets a suggestion point
  // straight at a specific leaf (e.g. a duration/amount variant) so
  // following it never requires an extra manual selection step.
  private findOptionById(id: string): DemoOption | undefined {
    for (const option of this.options) {
      if (option.id === id) {
        return option;
      }

      const subOption = this.isGroupOption(option)
        ? option.options.find((leaf) => leaf.id === id)
        : undefined;
      if (subOption) {
        return subOption;
      }
    }

    return undefined;
  }

  // Joins the storytelling copy with the live scenario list from the backend
  // (getScenarios()): the backend is the source of truth for which scenarios
  // exist and how long they run. Copy entries whose id no longer matches a
  // live scenario are dropped instead of shown as dead buttons.
  private resolveOptions(
    scenariosById: Map<string, ApiScenarioResponseDto>,
  ): DemoOption[] {
    const resolveLeaf = (copy: DemoLeafOptionCopy): DemoLeafOption | null => {
      const scenario = copy.id ? scenariosById.get(copy.id) : undefined;
      if (!scenario) {
        return null;
      }
      return { ...copy, loadingDurationSeconds: scenario.runtimeMinutes * 60 };
    };

    return DEMO_OPTIONS.reduce<DemoOption[]>((resolved, copy) => {
      if (copy.options) {
        const options = copy.options
          .map(resolveLeaf)
          .filter((option): option is DemoLeafOption => option !== null);
        if (options.length > 0) {
          resolved.push({ ...copy, options });
        }
        return resolved;
      }

      const leaf = resolveLeaf(copy);
      if (leaf) {
        resolved.push(leaf);
      }
      return resolved;
    }, []);
  }

  // Triggers the backend scenario that always runs at dialog start (kept out
  // of DEMO_OPTIONS so it never appears as a selectable card) and drives the
  // PROCESSING_INITIAL splash duration off its real runtimeMinutes. Falls
  // back to the old fixed-duration splash once the scenario list has
  // finished loading but doesn't contain it (misconfiguration), mirroring
  // the resilience of resolveOptions().
  private triggerInitialScenario(
    scenariosById: Map<string, ApiScenarioResponseDto>,
    isLoading: boolean,
  ) {
    if (this.initialScenarioTriggered) {
      return;
    }

    const initialScenario = scenariosById.get(INITIAL_SCENARIO_ID);
    if (initialScenario) {
      this.initialScenarioTriggered = true;
      this.store.dispatch(
        ScenarioActions.public.runScenario({ id: INITIAL_SCENARIO_ID }),
      );
      this.startLoading(
        DemoStep.SELECTION,
        initialScenario.runtimeMinutes * 60 * 1000,
      );
      return;
    }

    if (!isLoading) {
      this.initialScenarioTriggered = true;
      this.startLoading(DemoStep.SELECTION, 10000);
    }
  }

  private startLoading(
    nextStep: DemoStep.SELECTION | DemoStep.SUCCESS,
    durationMs: number,
  ) {
    this.step =
      nextStep === DemoStep.SELECTION
        ? DemoStep.PROCESSING_INITIAL
        : DemoStep.PROCESSING_FINAL;
    this.processingProgressPercent = 0;
    this.processingDurationMs = durationMs;
    this.processingElapsedMs = 0;
    this.clearTimers();

    const start = performance.now();
    const animate = (now: number) => {
      const elapsed = Math.min(durationMs, now - start);
      this.processingElapsedMs = elapsed;
      const percent = Math.min(100, (elapsed / durationMs) * 100);
      this.processingProgressPercent = percent;
      if (percent < 100) {
        this.animationFrameId = requestAnimationFrame(animate);
      } else {
        this.animationFrameId = null;
      }
    };
    this.animationFrameId = requestAnimationFrame(animate);

    this.timerId = setTimeout(() => {
      this.processingProgressPercent = 100;
      this.processingElapsedMs = durationMs;
      this.clearAnimationFrame();
      this.timerId = null;
      this.transitionTimerId = setTimeout(() => {
        this.transitionTimerId = null;

        // Before announcing success, offer the chained follow-up scenario
        // (if any is configured and not already followed) instead - success
        // is only shown once the user has decided on/skipped that offer.
        if (nextStep === DemoStep.SUCCESS && this.nextSuggestion) {
          this.step = DemoStep.SUGGESTION;
          return;
        }

        this.step = nextStep;
        if (nextStep === DemoStep.SUCCESS) {
          this.startSuccessAutoCloseTimer();
        }
      }, DialogDemoComponent.PROCESSING_POST_DELAY_MS);
    }, durationMs);
  }

  private clearTimeoutTimer() {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  private clearAnimationFrame() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  private clearTimers() {
    this.clearTimeoutTimer();
    this.clearTransitionTimer();
    this.clearSuccessAutoCloseTimer();
    this.clearAnimationFrame();
  }

  private clearTransitionTimer() {
    if (this.transitionTimerId) {
      clearTimeout(this.transitionTimerId);
      this.transitionTimerId = null;
    }
  }

  private startSuccessAutoCloseTimer() {
    this.clearSuccessAutoCloseTimer();
    this.successAutoCloseRemainingSeconds = Math.ceil(
      DialogDemoComponent.SUCCESS_AUTO_CLOSE_MS / 1000,
    );

    this.successAutoCloseIntervalId = setInterval(() => {
      this.successAutoCloseRemainingSeconds = Math.max(
        0,
        this.successAutoCloseRemainingSeconds - 1,
      );
    }, 1000);

    this.successAutoCloseTimerId = setTimeout(() => {
      this.successAutoCloseTimerId = null;
      this.clearSuccessAutoCloseInterval();
      if (this.step === DemoStep.SUCCESS) {
        this.dialogRef.close(true);
      }
    }, DialogDemoComponent.SUCCESS_AUTO_CLOSE_MS);
  }

  private clearSuccessAutoCloseTimer() {
    if (this.successAutoCloseTimerId) {
      clearTimeout(this.successAutoCloseTimerId);
      this.successAutoCloseTimerId = null;
    }
    this.clearSuccessAutoCloseInterval();
  }

  private clearSuccessAutoCloseInterval() {
    if (this.successAutoCloseIntervalId) {
      clearInterval(this.successAutoCloseIntervalId);
      this.successAutoCloseIntervalId = null;
    }
  }

  private formatDuration(durationMs: number): string {
    const totalSeconds = Math.max(0, Math.ceil(durationMs / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}
